import { Award, Cable, Compass, HeartHandshake, Play, Quote } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSiteAssets } from "@/lib/siteAssets";
import CableImage from "./CableImage";

const EXPERTISE = [
    "HT & LT Power Cables",
    "Copper & Aluminium Cables",
    "Armoured & Unarmoured Cables",
    "Flexible Cables",
    "House Wires",
    "Cable Termination Kits",
    "Electrical Accessories",
];

const BLOCKS = [
    {
        icon: Compass,
        title: "Our Vision",
        body: "To become one of India's most respected and trusted electrical cable distribution companies, recognized for quality, reliability, technical expertise, and customer satisfaction.",
    },
    {
        icon: HeartHandshake,
        title: "Our Commitment",
        body: "Every cable we supply powers homes, businesses, industries and infrastructure. Our commitment goes beyond supplying products — we strive to deliver trust, value, professional service and long-term partnerships with every customer.",
    },
];

export default function About() {
    const { images } = useSiteAssets();
    return (
        <section id="company" className="relative hud-bg py-20 lg:py-28 border-y border-line overflow-hidden" data-testid="company-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-2 gap-14 items-center mb-16">
                    <Reveal className="relative">
                        <div className="relative pb-12">
                            <div className="rounded-md border border-line overflow-hidden aspect-[4/3] w-[78%] shadow-lg">
                                <CableImage
                                    slot="about-shop"
                                    images={images}
                                    alt="Inside the Roxy Cables shop"
                                    label="Shop photo — upload from admin"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="rounded-md border border-line overflow-hidden aspect-[4/3] w-[52%] absolute bottom-0 right-0 shadow-2xl">
                                <CableImage
                                    slot="about-warehouse"
                                    images={images}
                                    alt="Cable drums at the Roxy Cables warehouse"
                                    label="Warehouse photo — upload from admin"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <span className="absolute top-4 -left-2 font-mono text-[10px] tracking-[0.25em] text-dim uppercase">
                                EST. CHICKPET, BENGALURU
                            </span>
                            <div className="absolute -bottom-1 left-4 bg-ink text-white rounded-md px-5 py-4 shadow-xl" data-testid="about-years-badge">
                                <p className="font-display font-bold text-3xl leading-none text-copper-bright">40+</p>
                                <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-white/60 mt-1">Years of expertise</p>
                            </div>
                        </div>
                    </Reveal>

                    <div>
                        <Reveal>
                            <Eyebrow className="mb-5">09 — About Us</Eyebrow>
                            <h2 className="font-display font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-ink mb-6">
                                Built on experience. <em className="text-copper">Driven by trust.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="text-mist leading-relaxed mb-4">
                                At Roxy Cables, we believe that strong connections are the foundation of a
                                brighter future. Founded by <span className="text-ink font-medium">Mr. Uttam Chand</span>,
                                a visionary entrepreneur with over 40 years of experience in the electrical cable
                                industry, Roxy Cables has been built on the values of integrity, reliability,
                                quality and lasting customer relationships.
                            </p>
                            <p className="text-mist leading-relaxed mb-8">
                                Headquartered in Bengaluru, Karnataka, we specialize in the distribution and supply
                                of high-quality electrical cables, wires and accessories — catering to residential,
                                commercial, industrial and large-scale infrastructure projects with the right
                                products, dependable solutions and exceptional service.
                            </p>
                        </Reveal>

                        <Reveal delay={0.15}>
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-dim mb-3 flex items-center gap-2">
                                <Cable size={12} className="text-copper" /> Our Expertise
                            </p>
                            <div className="flex flex-wrap gap-2" data-testid="expertise-badges">
                                {EXPERTISE.map((x) => (
                                    <span
                                        key={x}
                                        className="font-mono text-[10px] tracking-[0.12em] uppercase border border-copper/30 bg-copper-light px-3 py-1.5 rounded-full text-copper"
                                    >
                                        {x}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-16">
                    <Reveal className="md:row-span-1">
                        <div className="h-full rounded-md bg-ink text-white p-8 relative overflow-hidden" data-testid="founder-card">
                            <Quote size={60} className="absolute -top-2 -right-2 text-white/5" />
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-copper-bright mb-5 flex items-center gap-2">
                                <Award size={12} /> Founder's Legacy
                            </p>
                            <div className="w-20 h-20 rounded-full border-2 border-copper/40 overflow-hidden mb-5" data-testid="founder-photo-slot">
                                <CableImage
                                    slot="founder"
                                    images={images}
                                    alt="Mr. Uttam Chand, Founder of Roxy Cables"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <p className="font-display font-semibold text-2xl leading-snug mb-1">Mr. Uttam Chand</p>
                            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50 mb-5">Founder</p>
                            <p className="text-white/75 text-sm leading-relaxed mb-5">
                                With more than four decades of experience, Mr. Uttam Chand has established a strong
                                foundation of industry expertise, business ethics and customer trust.
                            </p>
                            <p className="text-white/90 text-sm leading-relaxed italic border-l-2 border-copper pl-4">
                                "Deliver quality, honour commitments, and build relationships that last for generations."
                            </p>
                        </div>
                    </Reveal>
                    {BLOCKS.map((b, i) => (
                        <Reveal key={b.title} delay={0.1 + i * 0.08}>
                            <div className="range-card h-full rounded-md border border-line bg-white p-8 transition-all duration-500" data-testid={`about-block-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <b.icon size={22} className="text-copper mb-5" />
                                <p className="font-display font-semibold text-xl text-ink mb-3">{b.title}</p>
                                <p className="text-mist text-sm leading-relaxed">{b.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal>
                    <div className="mb-16" data-testid="about-video-block">
                        <div className="flex items-center justify-between mb-5">
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-dim flex items-center gap-2">
                                <Play size={12} className="text-copper" /> Watch — how ordering works
                            </p>
                            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-dim">75 sec · sound on</span>
                        </div>
                        <div className="relative rounded-md border border-line bg-inkdark p-2 shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
                            <span className="range-corners" aria-hidden="true" />
                            <video
                                data-testid="about-walkthrough-video"
                                className="w-full rounded-sm aspect-video"
                                src="/files/roxy-cables-site-preview.mp4"
                                poster="/files/roxy-video-poster.jpg"
                                controls
                                preload="none"
                                playsInline
                            />
                        </div>
                    </div>
                </Reveal>

                <Reveal>
                    <div className="border-t border-line pt-10 text-center" data-testid="about-tagline">
                        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-dim mb-3">
                            40+ Years of Industry Expertise · A Legacy of Trust · A Future of Possibilities
                        </p>
                        <p className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-ink">
                            Connecting Today. <em className="text-copper">Powering Tomorrow.</em>
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
